import { useState } from 'react';
import Button from '../ui/Button';
import Card from '../ui/Card';

/**
 * Activity Settings Panel Component
 * Configure activity settings like time limits, participation mode, etc.
 */

const ActivitySettingsPanel = ({ activity, onUpdateSettings, onClose }) => {
  const [settings, setSettings] = useState({
    timeLimit: activity?.config?.timeLimit || 5,
    allowLateSubmissions: activity?.config?.allowLateSubmissions ?? true,
    showLiveResults: activity?.config?.showLiveResults ?? false,
    anonymousResponses: activity?.config?.anonymousResponses ?? false,
    requireAllParticipants: activity?.config?.requireAllParticipants ?? false,
    shuffleOptions: activity?.config?.shuffleOptions ?? false,
    passingScore: activity?.config?.passingScore || 70,
  });

  const handleChange = (key, value) => {
    setSettings((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleSave = () => {
    onUpdateSettings(settings);
    if (onClose) onClose();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h3 className="text-lg font-semibold text-neutral-900 mb-1">Activity Settings</h3>
        <p className="text-sm text-neutral-600">
          Configure how participants interact with this activity
        </p>
      </div>

      {/* Time Settings */}
      <Card padding="md" className="bg-neutral-50">
        <h4 className="text-sm font-semibold text-neutral-900 mb-3">Time Controls</h4>
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Time Limit (minutes)
            </label>
            <div className="flex items-center gap-3">
              <input
                type="range"
                min="1"
                max="30"
                value={settings.timeLimit}
                onChange={(e) => handleChange('timeLimit', parseInt(e.target.value))}
                className="flex-1"
              />
              <div className="w-20 px-3 py-2 bg-white border border-neutral-300 rounded-lg text-center font-semibold text-sm">
                {settings.timeLimit}m
              </div>
            </div>
          </div>

          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={settings.allowLateSubmissions}
              onChange={(e) => handleChange('allowLateSubmissions', e.target.checked)}
              className="mt-1 w-4 h-4 text-primary-600 rounded focus:ring-primary-500"
            />
            <div>
              <p className="text-sm font-medium text-neutral-900">Allow late submissions</p>
              <p className="text-xs text-neutral-500">Participants can submit after time limit</p>
            </div>
          </label>
        </div>
      </Card>

      {/* Display Settings */}
      <Card padding="md" className="bg-neutral-50">
        <h4 className="text-sm font-semibold text-neutral-900 mb-3">Display Options</h4>
        <div className="space-y-3">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={settings.showLiveResults}
              onChange={(e) => handleChange('showLiveResults', e.target.checked)}
              className="mt-1 w-4 h-4 text-primary-600 rounded focus:ring-primary-500"
            />
            <div>
              <p className="text-sm font-medium text-neutral-900">Show live results</p>
              <p className="text-xs text-neutral-500">Display results while activity is active</p>
            </div>
          </label>

          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={settings.anonymousResponses}
              onChange={(e) => handleChange('anonymousResponses', e.target.checked)}
              className="mt-1 w-4 h-4 text-primary-600 rounded focus:ring-primary-500"
            />
            <div>
              <p className="text-sm font-medium text-neutral-900">Anonymous responses</p>
              <p className="text-xs text-neutral-500">Hide participant names in results</p>
            </div>
          </label>

          {activity?.type === 'poll' && (
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={settings.shuffleOptions}
                onChange={(e) => handleChange('shuffleOptions', e.target.checked)}
                className="mt-1 w-4 h-4 text-primary-600 rounded focus:ring-primary-500"
              />
              <div>
                <p className="text-sm font-medium text-neutral-900">Shuffle options</p>
                <p className="text-xs text-neutral-500">Randomize option order for each participant</p>
              </div>
            </label>
          )}
        </div>
      </Card>

      {/* Participation Settings */}
      <Card padding="md" className="bg-neutral-50">
        <h4 className="text-sm font-semibold text-neutral-900 mb-3">Participation</h4>
        <div className="space-y-3">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={settings.requireAllParticipants}
              onChange={(e) => handleChange('requireAllParticipants', e.target.checked)}
              className="mt-1 w-4 h-4 text-primary-600 rounded focus:ring-primary-500"
            />
            <div>
              <p className="text-sm font-medium text-neutral-900">Require all participants</p>
              <p className="text-xs text-neutral-500">Wait for all participants before closing</p>
            </div>
          </label>
        </div>
      </Card>

      {/* Quiz-specific Settings */}
      {activity?.type === 'quiz' && (
        <Card padding="md" className="bg-neutral-50">
          <h4 className="text-sm font-semibold text-neutral-900 mb-3">Quiz Settings</h4>
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Passing Score (%)
            </label>
            <div className="flex items-center gap-3">
              <input
                type="range"
                min="0"
                max="100"
                step="5"
                value={settings.passingScore}
                onChange={(e) => handleChange('passingScore', parseInt(e.target.value))}
                className="flex-1"
              />
              <div className="w-20 px-3 py-2 bg-white border border-neutral-300 rounded-lg text-center font-semibold text-sm">
                {settings.passingScore}%
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Actions */}
      <div className="flex items-center gap-3">
        <Button variant="primary" fullWidth onClick={handleSave}>
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          Apply Settings
        </Button>
        {onClose && (
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
        )}
      </div>

      {/* Preview */}
      <Card padding="sm" className="bg-blue-50 border border-blue-200">
        <div className="flex items-start gap-2">
          <svg className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div className="flex-1">
            <p className="text-xs font-semibold text-blue-900 mb-1">Settings Preview</p>
            <ul className="text-xs text-blue-800 space-y-0.5">
              <li>• Time limit: {settings.timeLimit} minutes</li>
              {settings.showLiveResults && <li>• Live results enabled</li>}
              {settings.anonymousResponses && <li>• Anonymous mode active</li>}
              {settings.allowLateSubmissions && <li>• Late submissions allowed</li>}
              {activity?.type === 'quiz' && <li>• Passing score: {settings.passingScore}%</li>}
            </ul>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default ActivitySettingsPanel;
